import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CreateSubscriptionMethodMutation,
  useAttachPaymentMethodMutation,
  useConnectStripeAccountMutation,
  useCreateSubscriptionMethodMutation,
  useDisconnectStripeAccountMutation,
  useGetThirdPartyConfigQuery,
} from 'graphql/generated/hasura';
import { useGetPage } from './useGetPage';
import { pageIds } from 'utilities/constants';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import * as yup from 'yup';
import { useAuth } from '../auth/context/AuthContext';
import { capitalize } from 'lodash';
import { SUBSCRIPTION_ACTIVE_STATUS } from '../auth/context/AuthProvider';
import { useApolloClient } from '@apollo/client';
import moment from 'moment/moment';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCustomToast } from '../layout/useToast';
import { DASHBOARD } from '../utilities/routes';

export interface SubscriptionFormData {
  name: string;
}

export const CONFIRM_STRIPE_STATUSES = [
  'requires_action',
  'requires_confirmation',
];

export const useStripeConnect = (
  code: string | null,
  statePayload?: unknown,
) => {
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.BILLING_SERVICE_PROVIDER,
  });
  const isFirstRender = useRef(false);
  const { addToast } = useCustomToast();

  const { data } = useGetThirdPartyConfigQuery({
    context: {
      ignoreGlobalErrors: true,
    },
  });
  const clientId = useMemo(
    () => data?.frontEndAuthConfigs?.stripe?.clientId,
    [data],
  );

  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const returnUrl = useMemo(() => {
    const searchParams = new URLSearchParams(search);
    const urlState = searchParams.get('state');
    if (urlState) {
      try {
        const decodedState = JSON.parse(atob(urlState));
        if (decodedState?.returnUrl) {
          return decodedState.returnUrl;
        }
      } catch (e) {
        console.error('Failed to parse Stripe state param:', e);
      }
    }
    return pathname;
  }, [search, pathname]);

  const [connectStripeAccountMutation, { loading: connectLoading }] =
    useConnectStripeAccountMutation({
      refetchQueries: ['GetFHIRProviderbyEmail'],
      onCompleted: () => {
        navigate({ pathname: returnUrl, search: '' }, { replace: true });
      },
    });
  const [disconnectStripeAccount, { loading: disconectLoading }] =
    useDisconnectStripeAccountMutation({
      refetchQueries: ['GetFHIRProviderbyEmail'],
    });

  const encodedState = useMemo(() => {
    const stateObj = {
      payload: statePayload,
      returnUrl: pathname,
    };
    return btoa(JSON.stringify(stateObj));
  }, [statePayload, pathname]);

  const [isConnectedStripe, setIsConnectedStripe] = useState(false);
  const [showRedirectSpinner, setShowRedirectSpinner] = useState(false);
  const goToConnectStripePage = useCallback(() => {
    setShowRedirectSpinner(true);
    setTimeout(() => {
      const currentUrl = window.location.origin + DASHBOARD;
      const redirectUrl = `${currentUrl}`;

      window.location.href = `https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${clientId}&scope=read_write&redirect_uri=${encodeURIComponent(
        redirectUrl,
      )}${encodedState ? `&state=${encodeURIComponent(encodedState)}` : ''}`;
    }, 100);
  }, [clientId, encodedState]);

  const handleClickConnect = () => {
    goToConnectStripePage();
  };

  const handleClickDisconnect = () => {
    setShowRedirectSpinner(true);
    disconnectStripeAccount()
      ?.then(() => {
        addToast({
          message: locale?.successDisconnectStripeMessage,
          type: 'success',
        });
        setIsConnectedStripe(false);
      })
      ?.catch((error) => {
        addToast({
          message: 'Error trying to disconnect Stripe account',
          type: 'warning',
        });
        console.error('Disconnect stripe error: ', error);
      })
      ?.finally(() => setShowRedirectSpinner(false));
  };

  useEffect(() => {
    if (!code || isFirstRender.current) return;
    isFirstRender.current = true;
    connectStripeAccountMutation({
      variables: {
        code: code,
      },
    })
      .then(() => {
        addToast({
          message:
            locale?.successConnectStripeMessage ??
            'Your Stripe account has been successfully connected.',
          type: 'success',
        });
        setIsConnectedStripe(true);
      })
      .catch((error) => {
        addToast({
          message: 'Error trying to connect Stripe account',
          type: 'warning',
        });
        console.error('Connect stripe error: ', error);
      });
  }, [
    addToast,
    code,
    connectStripeAccountMutation,
    locale?.successConnectStripeMessage,
  ]);

  return {
    connectStripeAccountMutation,
    isConnectedStripe,
    showRedirectSpinner,
    connectLoading,
    disconectLoading,
    handleClickConnect,
    handleClickDisconnect,
  };
};

export const useCreateSubscriptionMethod = (
  setErrorMessage: (message: string) => void,
  callback: (date?: string) => void = () => ({}),
) => {
  const [success, setSuccess] = useState(false);
  const [processing, setProcessing] = useState(false);
  const { stripeSubscription } = useAuth();

  const stripe = useStripe();
  const elements = useElements();

  const [createSubMethod] = useCreateSubscriptionMethodMutation({
    refetchQueries: ['GetPaymentMethodList', 'GetStripeSubscriptions'],
    onCompleted: () => setErrorMessage(''),
    onError: (error: Error) => setErrorMessage(error.message),
  });

  const client = useApolloClient();

  const [attachPaymentMethod] = useAttachPaymentMethodMutation({
    refetchQueries: ['GetPaymentMethodList'],
    onCompleted: () => setErrorMessage(''),
    onError: (error: Error) => setErrorMessage(error.message),
  });

  const serverRequest = (name: string, id: string) => {
    const hasCurrentSubscriptionEnd = !SUBSCRIPTION_ACTIVE_STATUS.includes(
      stripeSubscription?.[0]?.status as number,
    );

    return hasCurrentSubscriptionEnd
      ? createSubMethod({
          variables: {
            paymentMethodName: `${capitalize(name)} Card`,
            stripePaymentMethodId: id,
          },
        })
      : attachPaymentMethod({
          variables: {
            paymentMethodName: `${capitalize(name)} Card`,
            stripePaymentMethodId: id,
          },
        });
  };

  const schema = yup
    .object({
      name: yup.string().required('Field is required'),
    })
    .required();

  const onSubmit = async () => {
    setProcessing(true);
    setErrorMessage('');
    setSuccess(false);

    if (!stripe || !elements) {
      setErrorMessage('Stripe.js not loaded');
      setProcessing(false);
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);
    const dateElement = elements.getElement(CardExpiryElement);
    const cvcElement = elements.getElement(CardCvcElement);

    if (!cardElement) {
      setErrorMessage('Card Element not found.');
      setProcessing(false);
      return;
    }

    const { error: paymentMethodError, paymentMethod } =
      await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

    if (paymentMethodError) {
      setErrorMessage(paymentMethodError.message ?? 'Error');
      setProcessing(false);
      return;
    }

    try {
      const { data: hasuraData } = await serverRequest(
        paymentMethod?.card?.brand ?? '',
        paymentMethod?.id,
      );

      const { clientSecret, status } =
        (hasuraData as CreateSubscriptionMethodMutation)?.createSubscription
          ?.paymentIntent ?? {};

      if (
        clientSecret &&
        !!status &&
        CONFIRM_STRIPE_STATUSES.includes(status)
      ) {
        const { error: confirmError } = await stripe.confirmCardPayment(
          clientSecret,
          {
            payment_method: paymentMethod.id,
          },
        );

        if (confirmError) {
          throw new Error(
            confirmError.message ?? 'Payment confirmation failed',
          );
        }

        client.refetchQueries({
          include: ['GetStripeSubscriptions'],
        });
        callback(
          moment(
            (hasuraData as CreateSubscriptionMethodMutation)?.createSubscription
              ?.currentPeriodEnd,
          ).format('DD.MM.YYYY'),
        );
      }

      if (hasuraData) {
        setSuccess(true);
        cardElement.clear();
        dateElement?.clear();
        cvcElement?.clear();
      }
    } catch (error) {
      console.log(error);
      setErrorMessage('Server error');
    } finally {
      setProcessing(false);
    }
  };

  return {
    onSubmit,
    processing,
    success,
    schema,
  };
};
